import {
  createServerDatabaseRootDocId,
  readDatabaseShardDirectoryDocIds,
  readDatabaseShardDirectoryLoaded,
} from '@streamlain/database-identity';

/** The parts of a BlockSuite workspace the Config tables are opened with. */
export type AppSettingsWorkspace = {
  id?: string;
  createDoc?: (docId: string) => AppSettingsWorkspaceDoc | null | undefined;
  getDoc?: (docId: string) => AppSettingsWorkspaceDoc | null | undefined;
  registerDoc?: (docId: string) => unknown;
  slots?: {
    docAdded?: {
      subscribe: (cb: () => void) => { unsubscribe: () => void };
    };
  };
};

export type AppSettingsYDoc = {
  getMap: (name: string) => any;
  getArray: (name: string) => any;
  on: (event: 'update', cb: () => void) => void;
  off: (event: 'update', cb: () => void) => void;
};

export type AppSettingsStore = {
  getBlock: (id: string) => { model?: any } | null | undefined;
  slots?: {
    blockUpdated?: {
      subscribe: (cb: () => void) => { unsubscribe: () => void };
    };
  };
  workspace?: AppSettingsWorkspace;
};

export type AppSettingsWorkspaceDoc = {
  getStore?: (options: { id: string }) => AppSettingsStore | null | undefined;
  load?: () => unknown;
  ready?: boolean;
  spaceDoc?: AppSettingsYDoc;
};

/** A document of the workspace, registered and loaded on first use. */
export function openWorkspaceDoc(
  workspace: AppSettingsWorkspace,
  docId: string
): AppSettingsWorkspaceDoc | null {
  let doc = workspace.getDoc?.(docId);
  if (!doc) {
    workspace.registerDoc?.(docId);
    doc = workspace.getDoc?.(docId) ?? workspace.createDoc?.(docId);
  }
  if (doc && !doc.ready) doc.load?.();
  return doc ?? null;
}

/**
 * The canonical root document of a database. App Container databases are
 * detached from the page, so they are read from here, not from a block.
 */
export function openDatabaseRootDoc(
  workspace: AppSettingsWorkspace,
  databaseId: string
): { doc: AppSettingsWorkspaceDoc; store: AppSettingsStore | null } | null {
  if (!workspace.id) return null;
  const rootDocId = createServerDatabaseRootDocId({
    workspaceId: workspace.id,
    databaseId,
  });
  const doc = openWorkspaceDoc(workspace, rootDocId);
  if (!doc) return null;
  return { doc, store: doc.getStore?.({ id: rootDocId }) ?? null };
}

function databaseRootModel(
  workspace: AppSettingsWorkspace,
  databaseId: string
) {
  return openDatabaseRootDoc(workspace, databaseId)?.store?.getBlock(databaseId)
    ?.model;
}

/**
 * The row shard directory of a database root; undefined until the root and
 * every directory part of it are loaded.
 */
export function databaseRootManifest(
  workspace: AppSettingsWorkspace,
  databaseId: string
) {
  const model = databaseRootModel(workspace, databaseId);
  const loaded = model && readDatabaseShardDirectoryLoaded(model);
  return loaded && !loaded.missingParts.length ? loaded.manifest : undefined;
}

/**
 * Calls back on every update of a database root and of its directory part
 * documents; parts the root lists later join on its next update.
 */
export function watchDatabaseRoot(
  workspace: AppSettingsWorkspace,
  databaseId: string,
  onChange: () => void
): () => void {
  const rootDoc = openDatabaseRootDoc(workspace, databaseId)?.doc.spaceDoc;
  let partDocs: AppSettingsYDoc[] = [];
  const unwatchParts = () => {
    for (const doc of partDocs) doc.off('update', update);
    partDocs = [];
  };
  const watchParts = () => {
    const model = databaseRootModel(workspace, databaseId);
    const docIds = model
      ? (readDatabaseShardDirectoryDocIds(model)?.partDocIds ?? [])
      : [];
    const docs = docIds.flatMap(docId => {
      const doc = openWorkspaceDoc(workspace, docId)?.spaceDoc;
      return doc ? [doc] : [];
    });
    if (
      docs.length === partDocs.length &&
      docs.every((doc, index) => doc === partDocs[index])
    )
      return;
    unwatchParts();
    partDocs = docs;
    for (const doc of docs) doc.on('update', update);
  };
  function update() {
    watchParts();
    onChange();
  }
  rootDoc?.on('update', update);
  watchParts();
  return () => {
    rootDoc?.off('update', update);
    unwatchParts();
  };
}
